<template>
  <div class="create-promo bg-white rounded-lg shadow-sm p-6 mb-10">
    <div class="mb-6">
      <h1 class="text-xl font-bold text-neutral-700">{{ isEdit ? 'Edit Promo' : 'Buat Promo Baru' }}</h1>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Nama Promo</label>
        <TextField
          type="text"
          placeholder="Masukkan nama promo"
          v-model="form.name"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Layanan Pengiriman</label>
        <CustomSelect
          :options="deliveryServices"
          :default="selectedDeliveryService"
          optionLabel="name"
          placeholder="Pilih layanan pengiriman"
          @input="val => selectedDeliveryService = val"
          @change="onDeliveryServiceChange"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Service / Paket</label>
        <v-select
            multiple 
            :options="packageList" 
            v-model="selectedPackages" 
            label="name"
            placeholder="Pilih service/paket"
            class="style-chooser"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Periode Promo</label>
         <date-range-picker
            v-model="dateRange"
            :locale-data="{ format: 'dd/mm/yyyy HH:MM', separator: ' - ' }"
            :timePicker="true"
            :timePicker24Hour="true"
            :showDropdowns="true"
            :ranges="false"
            class="w-full clean-daterange-picker"
        >
             <template v-slot:input="picker">
                <div class="w-full flex justify-between items-center rounded-lg border border-neutral-300 px-3 py-3">
                    <span class="text-sm text-neutral-700">{{ picker.startDate | date }} - {{ picker.endDate | date }}</span>
                    <Calendar />
                </div>
            </template>
        </date-range-picker>
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Persentase Diskon (%)</label>
        <TextField
          type="number"
          placeholder="ex: 10"
          v-model="form.discount_percentage"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Maksimal Nominal Diskon</label>
        <TextField
          type="number"
          placeholder="ex: 3000"
          v-model="form.max_discount_amount"
        />
      </div>

       <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Kuota</label>
        <TextField
          type="number"
          placeholder="ex: 50"
          v-model="form.quota"
        />
      </div>

       <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Kuota Tersedia</label>
        <TextField
          type="number"
          placeholder="ex: 50"
          v-model="form.available_quota"
          :disabled="!isEdit"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">Status</label>
        <div class="flex items-center gap-3">
            <Toggle 
                v-model="statusActive" 
                id="status-toggle"
            />
            <span class="text-sm font-medium">{{ statusActive ? 'Active' : 'Inactive' }}</span>
        </div>
      </div>
    </div>

    <div class="mt-8 flex justify-end gap-3">
        <button 
            @click="$router.push('/promo')"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
        >
            Batal
        </button>
        <button 
            @click="submit"
            class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50"
            :disabled="loading"
        >
            {{ loading ? 'Menyimpan...' : (isEdit ? 'Simpan Perubahan' : 'Simpan Promo') }}
        </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import DateRangePicker from "vue2-daterange-picker"
import "vue2-daterange-picker/dist/vue2-daterange-picker.css"
import dayjs from "dayjs"
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';

export default {
    name: 'CreatePromo',
    components: {
        DateRangePicker,
        vSelect,
        TextField: () => import('@/components/Forms/TextField'),
        CustomSelect: () => import('@/components/Forms/CustomSelect'),
        Calendar: () => import('@/components/Icons/Calendar'),
        Toggle: () => import('@/components/Toggle/Toggle'),
    },
    data: () => ({
        loading: false,
        form: {
            name: '',
            delivery_service_id: '',
            start_date: '',
            end_date: '',
            discount_percentage: '',
            max_discount_amount: '',
            quota: '',
            available_quota: '',
            status: 'Active',
            service_ids: []
        },
        statusActive: true,
        selectedDeliveryService: null,
        selectedPackages: [],
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        }
    }),
    watch: {
        statusActive(val) {
            this.form.status = val ? 'Active' : 'Inactive'
        },
        'form.quota'(val) {
            if (!this.isEdit) {
                this.form.available_quota = val
            }
        }
    },
    computed: {
        ...mapGetters("partner", ["deliveryServices", "packageList"]),
        ...mapGetters("promo", ["promoDetail"]),
        isEdit() {
            return !!this.$route.params.id
        }
    },
    filters: {
        date(val) {
            return val ? dayjs(val).format('DD MMM YYYY HH:mm') : ''
        }
    },
    async created() {
        await this.fetchDeliveryServices()
        if (this.isEdit) {
            this.fetchDetail()
        }
    },
    methods: {
        ...mapActions("partner", ["getDeliveryServices", "getPackages"]),
        ...mapActions("promo", ["createPromo", "getPromoDetail", "updatePromo"]),
        ...mapActions("common", ["showLoading", "hideLoading", "showExactToast"]),
        
        async fetchDeliveryServices() {
            this.showLoading()
            try {
                await this.getDeliveryServices()
            } catch (error) {
                console.error(error)
            } finally {
                this.hideLoading()
            }
        },
        async fetchDetail() {
            this.showLoading()
            try {
                await this.getPromoDetail({ id: this.$route.params.id })
                this.populateForm()
            } catch (error) {
                console.error(error)
                this.showExactToast({
                    message: "Gagal memuat data promo",
                    type: "error"
                })
            } finally {
                this.hideLoading()
            }
        },
        async populateForm() {
            const detail = this.promoDetail
            if (!detail) return

            this.form.name = detail.name
            
            // Set Delivery Service
            if (detail.delivery_service) {
                this.selectedDeliveryService = this.deliveryServices.find(ds => ds.id === detail.delivery_service.id)
                this.form.delivery_service_id = detail.delivery_service.id
                
                // Fetch packages for this delivery service
                await this.getPackages({ id: detail.delivery_service.id })
            }

            // Set Date
            const startDate = detail.start_date ? detail.start_date.replace('Z', '') : new Date()
            const endDate = detail.end_date ? detail.end_date.replace('Z', '') : new Date()

            this.dateRange = {
                startDate: new Date(startDate),
                endDate: new Date(endDate)
            }

            this.form.discount_percentage = detail.discount_percentage
            this.form.max_discount_amount = detail.max_discount_amount
            this.form.quota = detail.quota
            this.form.available_quota = detail.available_quota
            this.form.status = detail.status
            this.statusActive = detail.status === 'Active'

            // Set Selected Packages
            if (detail.packages) {
                this.selectedPackages = detail.packages
                this.form.service_ids = detail.packages.map(p => p.id)
            }
        },
        async onDeliveryServiceChange(val) {
            this.form.delivery_service_id = val ? val.id : ''
            this.selectedPackages = []
            this.form.service_ids = []
            
            if (val && val.id) {
                this.showLoading()
                try {
                    await this.getPackages({ id: val.id })
                } catch (error) {
                    console.error(error)
                } finally {
                    this.hideLoading()
                }
            } else {
                this.$store.commit('partner/setPackageList', [])
            }
        },
        validate() {
            if (!this.form.name) return 'Nama promo harus diisi'
            if (!this.form.delivery_service_id) return 'Layanan pengiriman harus dipilih'
            if (this.selectedPackages.length === 0) return 'Silakan pilih service/paket minimal satu'
            if (!this.form.discount_percentage) return 'Persentase diskon harus diisi'
            if (!this.form.max_discount_amount) return 'Maksimal nominal diskon harus diisi'
            if (!this.form.quota) return 'Kuota harus diisi'
            return null
        },
        async submit() {
            const error = this.validate()
            if (error) {
                 this.showExactToast({
                    message: error,
                    type: "error"
                })
                return
            }

            this.loading = true
            this.showLoading()

            const payload = {
                ...this.form,
                discount_percentage: Number(this.form.discount_percentage),
                max_discount_amount: Number(this.form.max_discount_amount),
                quota: Number(this.form.quota),
                available_quota: Number(this.form.available_quota),
                start_date: dayjs(this.dateRange.startDate).format('YYYY-MM-DD HH:mm:ss'),
                end_date: dayjs(this.dateRange.endDate).format('YYYY-MM-DD HH:mm:ss'),
                service_ids: this.selectedPackages.map(p => p.id)
            }

            try {
                if (this.isEdit) {
                     await this.updatePromo({ id: this.$route.params.id, payloads: payload })
                     this.showExactToast({
                        message: "Promo berhasil diperbarui",
                        type: "success"
                    })
                    this.$router.push('/promo')
                } else {
                    await this.createPromo({ payloads: payload })
                    this.showExactToast({
                        message: "Promo berhasil dibuat",
                        type: "success"
                    })
                    this.$router.push('/promo')
                }
            } catch (error) {
                console.error(error)
                 this.showExactToast({
                    message: error.response?.data?.message || "Gagal menyimpan promo",
                    type: "error"
                })
            } finally {
                this.loading = false
                this.hideLoading()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background: #dfe5fb;
  border: none;
  color: #394066;
  text-transform: lowercase;
  font-variant: small-caps;
}
::v-deep .vs__search {
    padding: 0.5rem 0.75rem; 
}
::v-deep .vs__dropdown-toggle {
    border-radius: 0.5rem;
    border: 1px solid rgba(206, 213, 222, 1);
    padding: 0;
}
.clean-daterange-picker ::v-deep .form-control {
    border: none;
    box-shadow: none;
    padding: 0;
    height: auto;
    background-color: transparent;
}
</style>
